import axios from "axios";
import { env } from "@/shared/config/Environment.jsx";

export const http = axios.create({
  baseURL: env.api.baseUrl,
  timeout: env.api.timeout,
  withCredentials: true,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

// Ensure CSRF header is always sent when the XSRF-TOKEN cookie exists
http.interceptors.request.use((config) => {
  // Axios may not always auto-set the header cross-site; enforce it
  if (!config.headers) config.headers = {};
  if (!config.headers["X-XSRF-TOKEN"]) {
    const match = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/);
    if (match) {
      try {
        config.headers["X-XSRF-TOKEN"] = decodeURIComponent(match[1]);
      } catch {
        config.headers["X-XSRF-TOKEN"] = match[1];
      }
    }
  }
  return config;
});

// Provide friendly fallback messages when backend did not supply one
http.interceptors.response.use(
  (response) => response,
  (error) => {
    const backendMessage = error?.response?.data?.message;
    if (!backendMessage) {
      const status = error?.response?.status;

      let message;
      if (!error?.response && error?.request) {
        message = "Network error. Please check your connection.";
      } else if (status === 419) {
        message = "Session expired. Please refresh and try again.";
      } else if (status === 429) {
        const retryAfter = error?.response?.headers?.["retry-after"];
        message = retryAfter
          ? `Too many attempts. Try again in ${retryAfter} seconds.`
          : "Too many attempts. Try again later.";
      } else if (status === 403) {
        message = "Action not allowed. If this persists, contact support.";
      } else if (status === 404) {
        message = "Resource not found.";
      } else if (status >= 500) {
        message = "Server error. Please try again later.";
      } else {
        message = "Request failed. Please try again.";
      }

      error.message = message;
    }
    return Promise.reject(error);
  },
);

export async function fetchCsrfToken() {
  // Sanctum's csrf-cookie endpoint lives at the app root, not under /api
  const base = env.api.baseUrl || "";
  const root = base.endsWith("/api") ? base.slice(0, -4) : base.replace(/\/api$/, "");
  await http.get("/sanctum/csrf-cookie", { baseURL: root });
}